/**
 * Global keyboard-shortcut layer for EquiTrack.
 *
 * Renders nothing on its own — sits in the React tree just below the
 * Router so it can call `useNavigate()` and own the help-dialog state.
 *
 * Bindings:
 *   g → d   Dashboard
 *   g → p   Portfolios
 *   g → r   Reports
 *   g → t   Tax Harvesting
 *   g → i   Insurance
 *   g → l   Lifeline
 *   g → u   My Uploads
 *   g → s   Support
 *   g → w   Wallet
 *   g → c   Settings (Config)
 *   ?       Show shortcuts help panel
 *   Shift+/ Same as ? — robust for Indian keyboard layouts where ? may
 *           not register as a top-level key on some Chromebooks /
 *           Linux distros
 *   Esc     Close shortcuts help panel
 *
 * Note: `Shift + E` (privacy mask toggle) lives in `PrivacyContext.js`
 * and stays there — co-located with the state it mutates. This file
 * is the index of EVERY shortcut so users can discover them.
 *
 * Guards (matching PrivacyContext's pattern):
 *   - bail when target is INPUT / TEXTAREA / SELECT / contenteditable
 *   - bail when any modal/dialog is currently open
 *   - bail when modifier keys other than Shift are pressed (so we never
 *     swallow Cmd+S, Ctrl+P, etc.)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GO_TARGETS = {
  d: { path: '/dashboard',     label: 'Dashboard' },
  p: { path: '/portfolios',    label: 'Portfolios' },
  r: { path: '/reports',       label: 'Reports' },
  t: { path: '/tax-harvesting', label: 'Tax Harvesting' },
  i: { path: '/insurance',     label: 'Insurance' },
  l: { path: '/lifeline',      label: 'Lifeline' },
  u: { path: '/my-uploads',    label: 'My Uploads' },
  s: { path: '/support',       label: 'Support' },
  w: { path: '/wallet',        label: 'Wallet' },
  c: { path: '/settings',      label: 'Settings (Config)' },
};

const SHORTCUT_GROUPS = [
  {
    title: 'Privacy',
    items: [
      { keys: ['Shift', 'E'], label: 'Toggle privacy mask (hide / show all figures)' },
    ],
  },
  {
    title: 'Navigation',
    items: Object.entries(GO_TARGETS).map(([k, t]) => ({
      keys: ['g', k.toUpperCase()],
      label: `Go to ${t.label}`,
    })),
  },
  {
    title: 'Help',
    items: [
      { keys: ['?'], label: 'Show this shortcut panel' },
      { keys: ['Shift', '/'], label: 'Show this shortcut panel (Indian-keyboard friendly)' },
      { keys: ['Esc'], label: 'Close any open dialog / drawer' },
    ],
  },
  {
    title: 'Touch Gestures (mobile / tablet / touchscreen)',
    items: [
      { keys: ['3-tap'], label: 'Tap any empty area 3 times — toggle privacy mask' },
      { keys: ['Double-tap'], label: 'Double-tap a holding row — open stock details' },
      { keys: ['Swipe ← →'], label: 'Inside a portfolio — swipe to switch sub-tabs (Equity / MF / F&O / FI)' },
      { keys: ['Swipe ← →'], label: 'On portfolio detail — swipe between your portfolios' },
      { keys: ['Long-press'], label: 'Long-press a hero figure (Net Worth / Invested / P/L / etc.) — reveal it for 3 seconds' },
    ],
  },
];

const isTyping = (target) => {
  if (!target) return false;
  const tag = (target.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
};

const isAnyDialogOpen = () =>
  Boolean(document.querySelector('[role="dialog"][data-state="open"]'));

export const KeyboardShortcuts = () => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);

  // ----------------------------------------------------------------
  // Per-user portfolio list — drives the `g + 1..9` shortcut. We hold
  // this in component state so the help dialog can render the live
  // mapping ("g + 1 → My Smallcase", etc.). Fetched once on mount and
  // refreshed whenever the dialog opens (cheap call, keeps the list
  // fresh after the user creates / deletes a portfolio elsewhere).
  // ----------------------------------------------------------------
  const [portfolios, setPortfolios] = useState([]);
  const refreshPortfolios = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/portfolios`, { withCredentials: true });
      if (Array.isArray(data)) {
        // Sort by `created_at` ascending so `g+1` always opens the
        // OLDEST portfolio (most stable mapping). Falls back to the
        // server's natural order when `created_at` is absent.
        const sorted = [...data].sort((a, b) => {
          const aT = a?.created_at || '';
          const bT = b?.created_at || '';
          if (aT && bT) return aT.localeCompare(bT);
          return 0;
        });
        setPortfolios(sorted);
      }
    } catch (_) { /* best-effort — non-fatal */ }
  }, []);
  useEffect(() => { refreshPortfolios(); }, [refreshPortfolios]);

  // Tracks whether the user just pressed `g` and we're waiting ≤1.5s for
  // the second key (sequence-style binding, à la Gmail / GitHub).
  const gWaitingRef = useRef(false);
  const gTimerRef = useRef(null);

  const cancelGWait = useCallback(() => {
    gWaitingRef.current = false;
    if (gTimerRef.current) {
      clearTimeout(gTimerRef.current);
      gTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      // Always allow Esc to close the help panel (independent of guards).
      if (e.key === 'Escape' && helpOpen) {
        setHelpOpen(false);
        e.preventDefault();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target)) return;
      if (isAnyDialogOpen() && !helpOpen) return;

      // Help panel: `?` (Shift+/) or plain `?`
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setHelpOpen((v) => !v);
        cancelGWait();
        return;
      }

      // From here, ignore Shift-modified keys — `Shift+E` is handled by
      // PrivacyContext; anything else with Shift shouldn't trigger nav.
      if (e.shiftKey) return;

      const key = (e.key || '').toLowerCase();

      // First key in a `g → X` sequence.
      if (!gWaitingRef.current && key === 'g') {
        gWaitingRef.current = true;
        // Cancel pending timer if any, then start a fresh 1.5s window.
        if (gTimerRef.current) clearTimeout(gTimerRef.current);
        gTimerRef.current = setTimeout(() => { cancelGWait(); }, 1500);
        e.preventDefault();
        return;
      }

      // Second key while waiting for `g → X`.
      if (gWaitingRef.current) {
        cancelGWait();
        // `g + 1..9` → nth portfolio (1-indexed, creation-order).
        // We accept this BEFORE the GO_TARGETS lookup so a future
        // alphabetic target named `1` (won't happen) can't shadow.
        if (/^[1-9]$/.test(key)) {
          const idx = parseInt(key, 10) - 1;
          if (portfolios[idx]) {
            e.preventDefault();
            navigate(`/portfolios/${portfolios[idx].id}`);
          }
          return;
        }
        const target = GO_TARGETS[key];
        if (target) {
          e.preventDefault();
          navigate(target.path);
        }
      }
    };
    // Also allow non-keyboard openers (sidebar hint, mobile header `?`
    // button) to surface the same dialog via a custom window event.
    // This keeps the dialog's state in one place without forcing every
    // caller to import a context.
    const onOpenEvent = () => {
      // Also refresh the portfolio list whenever an external opener
      // (sidebar hint, mobile `?` button) shows the dialog — same
      // freshness guarantee as the onOpenChange path.
      refreshPortfolios();
      setHelpOpen(true);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('equitrack:open-shortcuts', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('equitrack:open-shortcuts', onOpenEvent);
      if (gTimerRef.current) clearTimeout(gTimerRef.current);
    };
  }, [navigate, helpOpen, cancelGWait, portfolios, refreshPortfolios]);

  // Dynamic group: g + 1..9 → user's portfolios (creation-order). Built
  // each render so the help dialog reflects the latest list.
  const portfolioGroup = portfolios.length > 0 ? {
    title: 'Quick Portfolio Access',
    items: portfolios.slice(0, 9).map((p, idx) => ({
      keys: ['g', String(idx + 1)],
      label: p.name || `Portfolio ${idx + 1}`,
    })),
  } : null;
  const dialogGroups = portfolioGroup
    ? [SHORTCUT_GROUPS[0], SHORTCUT_GROUPS[1], portfolioGroup, ...SHORTCUT_GROUPS.slice(2)]
    : SHORTCUT_GROUPS;

  return (
    <Dialog
      open={helpOpen}
      onOpenChange={(open) => {
        // Refresh the portfolio list each time the dialog opens so the
        // dynamic `g+1..9` mapping never goes stale.
        if (open) refreshPortfolios();
        setHelpOpen(open);
      }}
    >
      <DialogContent
        className="max-w-lg p-0 flex flex-col"
        style={{ maxHeight: '85vh' }}
        data-testid="shortcuts-help-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <DialogTitle style={{ color: '#1B4332' }}>Keyboard Shortcuts</DialogTitle>
          <DialogDescription className="sr-only">
            Full list of keyboard shortcuts available in EquiTrack, grouped by feature.
          </DialogDescription>
        </DialogHeader>
        {/* Scrollable body — caps at 85% of the viewport on small/short
            screens so the shortcut list (which grows every release) never
            overflows the dialog or pushes the close button off-screen. */}
        <div
          className="px-6 pb-2 overflow-y-auto space-y-5 text-sm flex-1 min-h-0"
          data-testid="shortcuts-help-scroll-area"
        >
          {dialogGroups.map((group) => (
            <section key={group.title}>
              <h4
                className="text-[11px] uppercase tracking-wider mb-2"
                style={{ color: '#78716C', letterSpacing: '0.08em' }}
              >
                {group.title}
              </h4>
              <ul className="space-y-1.5">
                {group.items.map((item, i) => (
                  <li
                    key={`${group.title}-${i}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span style={{ color: '#1C1917' }}>{item.label}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {item.keys.map((k, j) => (
                        <React.Fragment key={`${k}-${j}`}>
                          {j > 0 && (
                            <span className="text-xs" style={{ color: '#A8A29E' }}>
                              {item.keys.length === 2 && (group.title === 'Navigation' || group.title === 'Quick Portfolio Access') ? 'then' : '+'}
                            </span>
                          )}
                          <kbd
                            className="px-2 py-0.5 rounded text-xs font-mono"
                            style={{
                              backgroundColor: '#F0F0EB',
                              color: '#1B4332',
                              border: '1px solid #E5E5DF',
                              minWidth: 24,
                              textAlign: 'center',
                              display: 'inline-block',
                            }}
                          >
                            {k}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        {/* Sticky footer note — stays visible while the body scrolls. */}
        <p
          className="text-xs px-6 py-3 shrink-0"
          style={{ color: '#78716C', borderTop: '1px solid #F0F0EB' }}
        >
          Shortcuts only fire when you aren't typing in an input and no dialog is open.
          Press <kbd className="px-1.5 py-0.5 rounded text-[11px] font-mono" style={{ backgroundColor: '#F0F0EB', color: '#1B4332' }}>?</kbd> any time to see this panel.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcuts;
